import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ApiError } from '@/types';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const INDUSTRIES = [
  'Plumbing',
  'HVAC',
  'Electrical',
  'Legal',
  'Medical',
  'Real Estate',
  'Financial Services',
  'Technology',
  'Consulting',
  'Marketing',
  'Construction',
  'Automotive',
  'Retail',
  'Other',
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

interface FormData {
  Account_Name: string;
  Industry: string;
  Is_AI_Receptionist_Free_Trial: boolean;
  First_Name: string;
  Last_Name: string;
  Phone: string;
  Email: string;
  Street: string;
  City: string;
  State: string;
  Postal_Code: string;
  Country: string;
  AI_Receptionist_Allowance: number;
  Human_Allowance: number;
  LiveChat_Allowance: number;
}

export function NewRequestPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<FormData>({
    Account_Name: '',
    Industry: '',
    Is_AI_Receptionist_Free_Trial: false,
    First_Name: '',
    Last_Name: '',
    Phone: '',
    Email: '',
    Street: '',
    City: '',
    State: '',
    Postal_Code: '',
    Country: 'US',
    AI_Receptionist_Allowance: 0,
    Human_Allowance: 0,
    LiveChat_Allowance: 0,
  });

  const handleChange = (field: keyof FormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent, submitImmediately = false) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Validate at least one allowance > 0
      if (
        formData.AI_Receptionist_Allowance === 0 &&
        formData.Human_Allowance === 0 &&
        formData.LiveChat_Allowance === 0
      ) {
        setError('At least one allowance must be greater than 0');
        setIsLoading(false);
        return;
      }

      const request = await api.createRequest(formData as unknown as Record<string, unknown>);

      if (submitImmediately) {
        await api.submitRequest(request.id);
      }

      navigate(`/requests/${request.id}`);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error || 'Failed to create request');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">New Request</h1>
            <p className="text-gray-600 mt-1">Create a new provisioning request</p>
          </div>
        </div>

        <form onSubmit={(e) => handleSubmit(e, false)}>
          {error && (
            <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600 mb-6">
              {error}
            </div>
          )}

          {/* Account Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>Basic information about the client account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="Account_Name">Account Name *</Label>
                  <Input
                    id="Account_Name"
                    value={formData.Account_Name}
                    onChange={(e) => handleChange('Account_Name', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="Industry">Industry *</Label>
                  <Select
                    value={formData.Industry}
                    onValueChange={(value) => handleChange('Industry', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((industry) => (
                        <SelectItem key={industry} value={industry}>
                          {industry}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="Is_AI_Receptionist_Free_Trial"
                  checked={formData.Is_AI_Receptionist_Free_Trial}
                  onChange={(e) => handleChange('Is_AI_Receptionist_Free_Trial', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-abby-primary focus:ring-abby-primary"
                />
                <Label htmlFor="Is_AI_Receptionist_Free_Trial">
                  AI Receptionist Free Trial
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>Primary contact for this account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="First_Name">First Name *</Label>
                  <Input
                    id="First_Name"
                    value={formData.First_Name}
                    onChange={(e) => handleChange('First_Name', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="Last_Name">Last Name *</Label>
                  <Input
                    id="Last_Name"
                    value={formData.Last_Name}
                    onChange={(e) => handleChange('Last_Name', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="Email">Email *</Label>
                  <Input
                    id="Email"
                    type="email"
                    value={formData.Email}
                    onChange={(e) => handleChange('Email', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="Phone">Phone</Label>
                  <Input
                    id="Phone"
                    type="tel"
                    value={formData.Phone}
                    onChange={(e) => handleChange('Phone', e.target.value)}
                    placeholder="+17025551234"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Address</CardTitle>
              <CardDescription>Business address (optional)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="Street">Street</Label>
                <Input
                  id="Street"
                  value={formData.Street}
                  onChange={(e) => handleChange('Street', e.target.value)}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="City">City</Label>
                  <Input
                    id="City"
                    value={formData.City}
                    onChange={(e) => handleChange('City', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="State">State</Label>
                  <Select
                    value={formData.State}
                    onValueChange={(value) => handleChange('State', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="Postal_Code">Postal Code</Label>
                  <Input
                    id="Postal_Code"
                    value={formData.Postal_Code}
                    onChange={(e) => handleChange('Postal_Code', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Allowances */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Service Allowances</CardTitle>
              <CardDescription>
                At least one allowance must be greater than 0
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="AI_Receptionist_Allowance">AI Receptionist</Label>
                  <Input
                    id="AI_Receptionist_Allowance"
                    type="number"
                    min="0"
                    value={formData.AI_Receptionist_Allowance}
                    onChange={(e) => handleChange('AI_Receptionist_Allowance', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="Human_Allowance">Human Receptionist</Label>
                  <Input
                    id="Human_Allowance"
                    type="number"
                    min="0"
                    value={formData.Human_Allowance}
                    onChange={(e) => handleChange('Human_Allowance', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="LiveChat_Allowance">Live Chat</Label>
                  <Input
                    id="LiveChat_Allowance"
                    type="number"
                    min="0"
                    value={formData.LiveChat_Allowance}
                    onChange={(e) => handleChange('LiveChat_Allowance', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={() => navigate('/')}>
              Cancel
            </Button>
            <Button type="submit" variant="secondary" disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save as Draft
            </Button>
            <Button
              type="button"
              disabled={isLoading}
              onClick={(e) => handleSubmit(e, true)}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save & Submit
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
